import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";
import {
    AUTO_LOCALE,
    createI18n,
    setActiveI18n,
    type I18n,
    type LocaleBundles,
    type LocaleParams,
} from "./index";

const I18nContext = createContext<I18n>(createI18n());

export interface I18nProviderProps {
    children: ReactNode;
    locale?: string | null;
    requestedLocale?: string | null;
    systemLocales?: readonly string[];
    bundles?: LocaleBundles;
}

export function I18nProvider({
    children,
    locale,
    requestedLocale,
    systemLocales,
    bundles,
}: I18nProviderProps) {
    const i18n = useMemo(
        () =>
            createI18n({
                requestedLocale: requestedLocale ?? locale ?? AUTO_LOCALE,
                systemLocales: systemLocales ?? readNavigatorLocales(),
                bundles,
            }),
        [bundles, locale, requestedLocale, systemLocales],
    );

    useEffect(() => {
        setActiveI18n(i18n);
        return () => setActiveI18n(null);
    }, [i18n]);

    useEffect(() => {
        if (typeof document === "undefined") return;
        document.documentElement.lang = i18n.locale;
        document.documentElement.dir = i18n.direction;
    }, [i18n.direction, i18n.locale]);

    return (
        <I18nContext.Provider value={i18n}>{children}</I18nContext.Provider>
    );
}

export function useI18n(): I18n {
    return useContext(I18nContext);
}

export function useT(
    namespace: string,
): (key: string, params?: LocaleParams) => string {
    const i18n = useI18n();
    return useMemo(
        () => (key: string, params?: LocaleParams) =>
            i18n.t(namespace, key, params),
        [i18n, namespace],
    );
}

function readNavigatorLocales(): string[] {
    if (typeof navigator === "undefined") return [];
    if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
        return [...navigator.languages];
    }
    return navigator.language ? [navigator.language] : [];
}
